// src/services/api/packingBatch.js
import { apiClient } from './base';

/**
 * Get user's accessible warehouses for packing batch operations
 */
export const getUserWarehouses = async () => {
  const response = await apiClient.get('/packing-batches/warehouses/accessible');
  return response.data;
};

/**
 * Search for orders eligible for packing batch
 */
export const searchEligibleOrders = async (params) => {
  const response = await apiClient.get('/packing-batches/orders/search', { params });
  return response.data;
};

/**
 * Create new packing batch without progress tracking
 */
export const createPackingBatch = async (batchData) => {
  const response = await apiClient.post('/packing-batches', batchData);
  return response.data;
};

// ─── Shared SSE reader helper ─────────────────────────────────────────────────

/**
 * Reads an SSE stream from `response` and dispatches events to the three
 * callbacks until the stream ends or an error occurs.
 *
 * @param {Response}  response   - Fetch Response whose body is an SSE stream
 * @param {Function}  onProgress - Called for each `type: 'progress'` event
 * @param {Function}  onComplete - Called once with `type: 'complete'` payload
 * @param {Function}  onError    - Called on stream errors or `type: 'error'`
 */
const readSseStream = async (response, onProgress, onComplete, onError) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last (possibly incomplete) line in the buffer
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(line.substring(6));

          if (data.type === 'complete') {
            onComplete?.(data.result);
          } else if (data.type === 'error') {
            onError?.(new Error(data.message));
          } else if (data.type === 'progress') {
            onProgress?.(data);
          }
        } catch (parseError) {
          console.error('Failed to parse SSE data:', parseError, line);
        }
      }
    }
  } catch (streamError) {
    console.error('SSE stream read error:', streamError);
    onError?.(streamError);
  }
};

// ─── Create packing batch with SSE ───────────────────────────────────────────

/**
 * Create new packing batch with SSE progress tracking.
 *
 * @param {Object}   batchData   - { businessCourierId, warehouseId, orderIds, notes }
 * @param {Function} onProgress  - Progress update callback
 * @param {Function} onComplete  - Completion callback receiving the result object
 * @param {Function} onError     - Error callback
 */
export const createPackingBatchWithProgress = async (batchData, onProgress, onComplete, onError) => {
  try {
    const token = localStorage.getItem('authToken');
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    const response = await fetch(`${apiUrl}/packing-batches?progress=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(batchData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create packing batch');
    }

    await readSseStream(response, onProgress, onComplete, onError);
  } catch (error) {
    console.error('SSE Error (createPackingBatch):', error);
    onError?.(error);
    throw error;
  }
};

// ─── Add orders to batch with SSE ────────────────────────────────────────────

/**
 * Add orders to existing packing batch with SSE progress tracking.
 */
export const addOrdersToPackingBatch = async (batchId, orderIds) => {
  const response = await apiClient.post(`/packing-batches/${batchId}/orders`, { orderIds });
  return response.data;
};

export const addOrdersToPackingBatchWithProgress = async (
  batchId,
  orderIds,
  onProgress,
  onComplete,
  onError
) => {
  try {
    const token = localStorage.getItem('authToken');
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    const response = await fetch(`${apiUrl}/packing-batches/${batchId}/orders?progress=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ orderIds })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to add orders to batch');
    }

    await readSseStream(response, onProgress, onComplete, onError);
  } catch (error) {
    console.error('SSE Error (addOrdersToBatch):', error);
    onError?.(error);
    throw error;
  }
};

// ─── Retry failed orders ──────────────────────────────────────────────────────

/**
 * Retry failed orders — plain JSON response (no progress stream).
 * Kept for backward-compatibility.
 */
export const retryFailedOrders = async (batchId, orderIds = []) => {
  const response = await apiClient.post(`/packing-batches/${batchId}/retry`, { orderIds });
  return response.data;
};

/**
 * Retry failed orders with SSE progress streaming.
 *
 * Use this instead of `retryFailedOrders` when retrying batches with many
 * orders so the browser does not time out waiting for a response.
 *
 * @param {string|number} batchId    - Packing batch ID
 * @param {number[]}      orderIds   - IDs of failed orders to retry (empty = all failed)
 * @param {Function}      onProgress - Called for each `type: 'progress'` event
 * @param {Function}      onComplete - Called once with the final result object
 * @param {Function}      onError    - Called on error
 */
export const retryFailedOrdersWithProgress = async (
  batchId,
  orderIds = [],
  onProgress,
  onComplete,
  onError
) => {
  try {
    const token = localStorage.getItem('authToken');
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    const response = await fetch(
      `${apiUrl}/packing-batches/${batchId}/retry?progress=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ orderIds })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to retry orders');
    }

    await readSseStream(response, onProgress, onComplete, onError);
  } catch (error) {
    console.error('SSE Error (retryFailedOrders):', error);
    onError?.(error);
    throw error;
  }
};

// ─── Remaining CRUD helpers ───────────────────────────────────────────────────

export const getAllPackingBatches = async (params = {}) => {
  const response = await apiClient.get('/packing-batches', { params });
  return response.data;
};

export const getPackingBatchById = async (batchId) => {
  const response = await apiClient.get(`/packing-batches/${batchId}`);
  return response.data;
};

/**
 * Get the aggregated product pick list for a packing batch
 * (total quantity per product/variation needed to pack the batch's orders).
 */
export const getPackingBatchProductList = async (batchId) => {
  const response = await apiClient.get(`/packing-batches/${batchId}/product-list`);
  return response.data;
};

/**
 * Get print data for airwaybill labels.
 * @param {Object} params - { packingBatchId? } or { orderIds? }
 */
export const getPackingBatchPrintData = async (params) => {
  const response = await apiClient.get('/packing-batches/print-data', { params });
  return response.data;
};

export const getBatchStatistics = async (params = {}) => {
  const response = await apiClient.get('/packing-batches/statistics/summary', { params });
  return response.data;
};

export const updateWaybillRange = async (businessCourierId, waybillData) => {
  const response = await apiClient.put(
    `/packing-batches/business-couriers/${businessCourierId}/waybill-range`,
    waybillData
  );
  return response.data;
};

export const updateApiConfiguration = async (businessCourierId, apiConfigData) => {
  const response = await apiClient.put(
    `/packing-batches/business-couriers/${businessCourierId}/api-config`,
    apiConfigData
  );
  return response.data;
};

/**
 * Get packing and dispatch dashboard summary.
 * @param {Object} params - { warehouseId?, dateFrom?, dateTo? }
 */
export const getDashboardSummary = async (params = {}) => {
  const response = await apiClient.get('/packing-batches/dashboard-summary', { params });
  return response.data;
};

/**
 * Get payment records for a specific order (for payment popup in packing batch page).
 * @param {number} orderId
 */
export const getOrderPaymentRecords = async (orderId) => {
  const response = await apiClient.get(`/packing-batches/orders/${orderId}/payment-records`);
  return response.data;
};

/**
 * Mark a packing batch as printed.
 * Called when user clicks the Print Labels button on the print waybills page.
 * @param {number|string} batchId
 */
export const markBatchAsPrinted = async (batchId) => {
  const response = await apiClient.put(`/packing-batches/${batchId}/mark-printed`);
  return response.data;
};